<script lang="ts">
  // « Membres » of a conversation, in the right panel (docs/spec.md, Précisions apportées à
  // l'étape 3, Droits): any member adds people to a channel; a channel admin removes
  // someone; everyone leaves a channel or a group. Nobody is added to a direct message or a
  // group: one opens a new group instead.
  import { api, ApiError, type Conversation } from '../lib/api';
  import { router } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import Avatar from './Avatar.svelte';

  let { conversation, onClose }: { conversation: Conversation; onClose: () => void } = $props();

  const channel = $derived(conversation.kind === 'public' || conversation.kind === 'private');
  const me = $derived(app.me?.id ?? '');
  const canManage = $derived(conversation.role === 'admin' || !!app.me?.is_admin);
  const archived = $derived(!!conversation.archived_at);
  const members = $derived(
    [...conversation.members].sort(
      (a, b) =>
        Number(conversation.admin_ids.includes(b.id)) - Number(conversation.admin_ids.includes(a.id)) ||
        a.display_name.localeCompare(b.display_name, 'fr'),
    ),
  );

  let error = $state('');
  let adding = $state(false);
  let query = $state('');
  let chosen = $state<string[]>([]);
  let busy = $state(false);

  const candidates = $derived(
    Object.values(app.users)
      .filter((u) => !u.is_bot && !conversation.members.some((m) => m.id === u.id))
      .filter((u) => {
        const q = query.trim().toLowerCase();
        return !q || u.display_name.toLowerCase().includes(q) || u.username.includes(q);
      })
      .sort((a, b) => a.display_name.localeCompare(b.display_name, 'fr')),
  );

  const message = (e: unknown) => (e instanceof ApiError ? e.message : 'Une erreur est survenue');

  async function add() {
    if (chosen.length === 0) return;
    busy = true;
    error = '';
    try {
      app.upsertConversation(await api.addMembers(conversation.id, chosen));
      chosen = [];
      query = '';
      adding = false;
    } catch (e) {
      error = message(e);
    } finally {
      busy = false;
    }
  }

  async function remove(id: string, name: string) {
    if (!confirm(`Retirer ${name} de ${conversation.title} ? Ses messages restent ; ses tâches ouvertes n'auront plus de responsable.`)) return;
    error = '';
    try {
      await api.removeMember(conversation.id, id);
      await app.readElsewhere(conversation.id);
    } catch (e) {
      error = message(e);
    }
  }

  async function leave() {
    const what = channel ? 'ce canal' : 'ce groupe';
    const hint = conversation.kind === 'private' || conversation.kind === 'group' ? " Vous n'y aurez plus accès sans qu'un membre vous ajoute." : '';
    if (!confirm(`Quitter ${what} ?${hint}`)) return;
    error = '';
    try {
      await api.removeMember(conversation.id, me);
      app.removeConversation(conversation.id);
      router.go('/');
    } catch (e) {
      error = message(e);
    }
  }
</script>

<aside class="panel" aria-label="Membres">
  <header>
    <h2>Membres <span class="where">· {conversation.members.length}</span></h2>
    <button type="button" class="close" aria-label="Fermer les membres" onclick={onClose}>
      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
  </header>

  <div class="scroll">
    {#if error}<p class="error" role="alert">{error}</p>{/if}

    {#if channel && !archived}
      {#if adding}
        <div class="add">
          <label class="sr-only" for="add-search">Chercher une personne à ajouter</label>
          <input id="add-search" class="input" placeholder="Chercher une personne" bind:value={query} />
          <ul class="candidates" aria-label="Personnes à ajouter">
            {#each candidates as u (u.id)}
              <li>
                <label>
                  <input type="checkbox" value={u.id} bind:group={chosen} />
                  <Avatar user={u} size={24} />
                  <span class="name">{u.display_name}</span>
                  <span class="muted">@{u.username}</span>
                </label>
              </li>
            {:else}
              <li class="muted">{query.trim() ? 'Personne ne correspond.' : 'Tout le monde est déjà membre.'}</li>
            {/each}
          </ul>
          <div class="row">
            <button type="button" class="btn" onclick={() => { adding = false; chosen = []; }}>Annuler</button>
            <button type="button" class="btn primary" disabled={busy || chosen.length === 0} onclick={add}>
              Ajouter{chosen.length ? ` (${chosen.length})` : ''}
            </button>
          </div>
        </div>
      {:else}
        <button type="button" class="btn primary add-btn" onclick={() => (adding = true)}>
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 5v14M5 12h14" /></svg>
          Ajouter des personnes
        </button>
      {/if}
    {:else if conversation.kind === 'group'}
      <p class="muted small">On n'ajoute personne à un groupe : ouvrez un nouveau groupe avec « + ».</p>
    {/if}

    <ul class="members">
      {#each members as m (m.id)}
        <li>
          <Avatar user={m} size={32} presence={app.presence[m.id]} />
          <span class="who">
            <span class="name">
              {m.display_name}{m.id === me ? ' (vous)' : ''}
              {#if channel && conversation.admin_ids.includes(m.id)}<span class="tag">Admin</span>{/if}
            </span>
            <span class="muted">@{m.username}</span>
          </span>
          {#if channel && canManage && !archived && m.id !== me}
            <button type="button" class="link danger" onclick={() => remove(m.id, m.display_name)}>Retirer</button>
          {/if}
        </li>
      {/each}
    </ul>
  </div>

  {#if conversation.kind !== 'direct'}
    <footer>
      <button type="button" class="link danger" onclick={leave}>Quitter {channel ? 'le canal' : 'le groupe'}</button>
    </footer>
  {/if}
</aside>

<style>
  .panel {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg);
    border-left: 1px solid var(--border);
    min-width: 0;
  }
  header {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: var(--header-h);
    padding: 8px 12px 8px 20px;
    border-bottom: 1px solid var(--border);
  }
  h2 {
    flex: 1;
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }
  .where {
    color: var(--muted);
    font-weight: 400;
  }
  .close {
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .add-btn {
    align-self: flex-start;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .add {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
  }
  .candidates {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 260px;
    overflow-y: auto;
  }
  .candidates label {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
    padding: 0 4px;
    border-radius: var(--radius-s);
    cursor: pointer;
  }
  .candidates label:hover {
    background: var(--bg-hover);
  }
  .row {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
  .members {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .members li {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
  }
  .who {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .name {
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  .muted {
    color: var(--muted);
    font-size: 12px;
  }
  .small {
    margin: 0;
    font-size: 13px;
  }
  .tag {
    font-size: 11px;
    font-weight: 600;
    padding: 0 6px;
    border-radius: 8px;
    background: var(--accent-soft);
    color: var(--accent-on-soft);
  }
  .link {
    border: 0;
    background: none;
    padding: 4px;
    font-size: 13px;
    font-weight: 600;
    color: var(--accent-text);
  }
  .link.danger {
    color: var(--danger);
  }
  footer {
    padding: 12px 20px 16px;
    border-top: 1px solid var(--border);
  }
</style>
